<template>
  <div class="kiosk-flow-container">
    <!-- Header / Back Button Area (only show for steps 1 and 2) -->
    <div
      v-if="currentStep !== 3"
      class="flow-header"
    >
      <div class="header-left">
        <button
          class="back-btn btn-reset"
          @click="handleBack"
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-arrow-left"
            aria-hidden="true"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </button>
      </div>

      <div
        class="header-title"
        v-if="stepTitle"
      >
        {{ stepTitle }}
      </div>

      <div class="header-right">
        <button
          class="cancel-btn btn-reset"
          @click="handleCancel"
          type="button"
        >
          Cancel Order
        </button>
      </div>
    </div>

    <!-- Steps Content -->
    <div class="flow-content">
      <!-- Step 1: Scheduling -->
      <StepScheduling
        v-if="currentStep === 1"
        :dining-option="diningOption"
        :initial-pickup-settings="kioskData.pickupSettings"
        :initial-serve-at="kioskData.serveAt"
        @update="handleSchedulingUpdate"
        @next="handleSchedulingNext"
      />

      <!-- Step 2: Customer Details -->
      <StepCustomerDetails
        v-if="currentStep === 2"
        :initial-customer-data="kioskData.customer"
        @update="handleDetailsUpdate"
        @next="handleDetailsNext"
        @back="handleBack"
      />

      <!-- Step 3: Payment -->
      <StepPayment
        v-if="currentStep === 3"
        :total="total"
        :subtotal="subtotal"
        :tax-total="taxTotal"
        :currency="currency"
        :scheduling-text="schedulingSummary"
        :customer="kioskData.customer"
        :payment-types="paymentTypes"
        :is-processing="isProcessing"
        :confirmed-order="confirmedOrder"
        @initiate-payment="handleInitiatePayment"
        @back="handleBack"
        @cancel="handleCancel"
      />
    </div>
  </div>
</template>

<script>
import StepScheduling from "./CheckoutSteps/Step1Scheduling.vue";
import StepCustomerDetails from "./CheckoutSteps/Step2CustomerDetails.vue";
import StepPayment from "./CheckoutSteps/Step3Payment.vue";
import moment from "moment";

export default {
  name: "KioskCheckoutStepper",
  components: {
    StepScheduling,
    StepCustomerDetails,
    StepPayment,
  },
  props: {
    diningOption: {
      type: Object,
      default: null,
    },
    total: {
      type: Number,
      default: 0,
    },
    subtotal: {
      type: Number,
      default: null,
    },
    taxTotal: {
      type: Number,
      default: null,
    },
    currency: {
      type: String,
      default: "$",
    },
    paymentTypes: {
      type: Array,
      default: () => [],
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
    confirmedOrder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentStep: 1,
      kioskData: {
        serveAt: null,
        pickupSettings: "now",
        customer: {
          name: "",
          phone: "",
        },
      },
    };
  },
  computed: {
    kioskAskName() {
      return this.$store.getters["config/kioskAskName"];
    },
    schedulingSummary() {
      if (this.kioskData.pickupSettings === "now") {
        return "Preparing Immediately";
      }
      if (this.kioskData.serveAt) {
        return moment(this.kioskData.serveAt).format("MMM D, h:mm A");
      }
      return "Scheduled";
    },
    stepTitle() {
      switch (this.currentStep) {
        case 1:
          return "Checkout Preferences";
        case 2:
          return "Your Details";
        case 3:
          return this.paymentTypes.length === 1
            ? "Confirm Order"
            : "Select Payment Method";
        default:
          return "";
      }
    },
  },
  watch: {
    diningOption: {
      handler(newVal) {
        if (newVal && this.currentStep === 1) {
          this.initStep();
        }
      },
      immediate: true,
    },
  },
  methods: {
    initStep() {
      if (!this.diningOption) return;

      // Check if scheduling is enabled
      const isSchedulingEnabled =
        this.diningOption.is_scheduling_enabled === true;

      // If scheduling is not enabled, skip Step 1 and default to "now"
      if (!isSchedulingEnabled) {
        // Emit default scheduling data
        this.$emit("update-scheduling", {
          serveAt: null,
          pickupSettings: "now",
        });

        // Determine next step based on kioskAskName
        if (this.kioskAskName) {
          this.currentStep = 2;
        } else {
          this.currentStep = 3;
        }
      }
    },
    handleSchedulingNext(data) {
      this.kioskData.serveAt = data.serveAt;
      this.kioskData.pickupSettings = data.pickupSettings;

      // Emit update to parent to sync with order
      this.$emit("update-scheduling", data);

      if (this.kioskAskName) {
        this.currentStep = 2;
      } else {
        this.currentStep = 3;
      }
    },
    handleSchedulingUpdate(data) {
      this.kioskData.serveAt = data.serveAt;
      this.kioskData.pickupSettings = data.pickupSettings;
    },
    handleCancel() {
      this.$emit("cancel-order");
    },
    handleDetailsUpdate(data) {
      this.kioskData.customer = data.customer;
    },
    handleDetailsNext(data) {
      this.kioskData.customer = data.customer;

      // Emit update to parent
      this.$emit("update-customer", data);

      this.currentStep = 3;
    },
    handleBack() {
      const isSchedulingEnabled =
        this.diningOption && this.diningOption.is_scheduling_enabled === true;

      if (this.currentStep === 1) {
        this.$emit("close");
      } else if (this.currentStep === 2) {
        if (!isSchedulingEnabled) {
          this.$emit("close");
        } else {
          this.currentStep = 1;
        }
      } else if (this.currentStep === 3) {
        if (this.kioskAskName) {
          this.currentStep = 2;
        } else {
          if (!isSchedulingEnabled) {
            this.$emit("close");
          } else {
            this.currentStep = 1;
          }
        }
      }
    },
    handleInitiatePayment(paymentMethod) {
      this.$emit("initiate-payment", paymentMethod);
    },
  },
};
</script>

<style lang="scss" scoped>
.kiosk-flow-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8f9fa;
  position: relative;
}

.flow-header {
  padding: 1.25rem;
  background: #fff;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  width: 100%;
  min-height: 70px;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #f3f4f6;
  justify-content: space-between;
}

.header-left,
.header-right {
  width: 8rem;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 700;
  font-family: serif;
  color: #111827;
}

.btn-reset {
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  margin: 0;
}

.back-btn {
  font-size: 1.25rem;
  color: #4b5563;
  text-decoration: none;
  font-weight: 600;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f3f4f6;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #e5e7eb;
  transform: scale(0.95);
}

.cancel-btn {
  color: #ef4444;
  background: #fef2f2;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  white-space: nowrap;
  transition: all 0.2s;
}

.cancel-btn:hover {
  color: #dc2626;
}

.flow-content {
  flex: 1;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.flow-content:not(:has(.kiosk-step-payment)) {
  padding-top: 70px;
}

::v-deep .flow-content > div {
  height: 100%;
}

.flow-content ::v-deep .form-group {
  label {
    text-align: left;
  }
}
</style>
